import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Role } from '@tp-vision/roles-permissions';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { userCreateValidationSchema } from '~components/organization/utils';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useServiceAdminUserCreateMutation } from './__generated__/AddUserModal.graphql';
import {
  getEditableRoleOptions,
  getUserManagementErrorMap,
  roleOptionsSchema,
  RolesField,
  toEditableRoleOptions,
} from './userManagement';

const schema = z.object({ ...userCreateValidationSchema, roles: roleOptionsSchema });

type FormValues = z.infer<typeof schema>;

interface Props {
  organization: { id: string; name: string; developerAccess: boolean } | null;
  onClose: () => void;
}

export function AddUserModal({ organization, onClose }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [createUser] = useServiceAdminUserCreateMutation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset({ givenName: '', familyName: '', email: '', roles: toEditableRoleOptions([Role.Guest]) });
  }, [organization, reset]);

  const onSubmit = async ({ givenName, familyName, email, roles }: FormValues) => {
    if (!organization) {
      return;
    }

    try {
      await createUser({
        variables: {
          organizationId: organization.id,
          input: { givenName, familyName, email, roles: roles.map(({ value }) => value) },
        },
      });

      toast({
        status: 'success',
        title: (
          <Trans
            i18nKey="adminSearchUserInvited"
            values={{
              name: `${givenName} ${familyName}`,
              organizationName: organization.name,
              email,
            }}
            components={{ strong: <strong /> }}
          />
        ),
      });

      onClose();
    } catch (err) {
      toast({
        status: 'error',
        title: t('adminSearchUserInviteError'),
        description: fromError(err, 'ServiceAdminUserCreate', getUserManagementErrorMap()),
      });
    }
  };

  return (
    <Modal isOpen={Boolean(organization)} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>{t('addUser')}</ModalHeader>
        <ModalCloseButton onClick={onClose} />
        <ModalBody>
          <Stack spacing="5">
            <FormControl isInvalid={Boolean(errors.givenName)}>
              <FormLabel>{t('firstName')}</FormLabel>
              <Input {...register('givenName')} />
              <FormErrorMessage>{errors.givenName?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.familyName)}>
              <FormLabel>{t('lastName')}</FormLabel>
              <Input {...register('familyName')} />
              <FormErrorMessage>{errors.familyName?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel>{t('email')}</FormLabel>
              <Input type="email" {...register('email')} />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
            <RolesField
              control={control}
              name="roles"
              error={errors.roles as never}
              options={getEditableRoleOptions({ developerAccess: organization?.developerAccess })}
            />
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" marginRight="3" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
            {t('addUser')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

AddUserModal.graphql = {
  mutations: {
    ServiceAdminUserCreate: gql`
      mutation ServiceAdminUserCreate($organizationId: ID!, $input: ServiceAdminUserCreateInput!) {
        serviceAdminUserCreate(organizationId: $organizationId, input: $input) {
          id
        }
      }
    `,
  },
};
